import { guid } from "../util/Guid"

import { servicesAPI } from "./api"

import { CollectionView, IItem } from "../models"
import { IButton, ICallButton } from "../components/common/contextmenu"
import {
    GetItemBoxDataResponseWrapper,
    GetItemsResponseWrapper,
    ItemActionsData,
    ItemActionsDataValue,
    ItemBoxItem,
} from "./models/itembox"
import { ServicesResponse } from "./models/api/services"
import { Labels } from "../util/Translations"
import { getItem, getItemConcept, getItemParent } from "./items"
import { getItemDetailSimplePrintUrl } from "../util/UrlGenerator"
import env from "../env"
import { ITenForceWindow } from "../global"

const AttachmentsKey = "Attachments"
const AttachmentsEditableKey = "AreAttachmentsEditable"
const AttachmentsEnabledKey = "AreAttachmentsEnabled"
const IsNumberEnabledKey = "IsNumberEnabled"
const ItemIdKey = "ItemId"
const ListIdKey = "ListId"
const ListNameKey = "ListName"
const NumberKey = "Nr"
const StatusKey = "Status"
const TitleKey = "Title"
const WorkspaceIdKey = "WorkspaceId"

declare let window: ITenForceWindow

export const getCardViewData = (
    selectionFilterId: string,
    pageIndex = 0,
    smartListContext = -1,
    pageSize: number,
): Promise<CollectionView> => {
    const payload = {
        selectionFilterId,
        pageIndex,
        smartListContext,
        pageSize,
    }

    return servicesAPI.postJSON(
        "GetItemBoxDataForCardView",
        payload,
        parseCardViewData,
    )
}

export const getCardViewItems = (
    selectionFilterId: string,
    itemIds: number[],
): Promise<IItem[]> => {
    const payload = {
        selectionFilterId,
        itemIds,
    }

    return servicesAPI.postJSON(
        "CollectItemsForCardView",
        payload,
        parseCardViewItems,
    )
}

export const getCardViewItemActions = (itemId: number): Promise<IButton[]> => {
    const payload = {
        itemId,
    }

    return servicesAPI.postJSON(
        "GetItemActionsForCardView",
        payload,
        parseItemActionsData,
    )
}

export const executeCardViewItemAction = async (
    itemId: number,
    action: string,
    shouldSkipOldUIBehavior = true,
): Promise<string> => {
    const payload = {
        itemid: `${itemId}i`,
        action,
    }

    // The inline collections in the NewUI don't have the proper data injected in the page.
    // Because of that, we are trying to handle these actions here.
    // At some point, this will need to be replaced by DataAPI actions.
    if (shouldSkipOldUIBehavior) {
        const item = await getItem(itemId)
        const parent = await getItemParent(itemId)
        const concept = await getItemConcept(itemId)
        const CNI = window.cni.getInstance()
        switch (action) {
            case "CopyItemAction":
                CNI.clear()
                CNI.setAfterLoadingFields(function () {
                    if (CNI.title) {
                        CNI.title.value = CNI.createCopyTitle(
                            item?.title ?? "",
                            true,
                        )
                    }
                })
                CNI.setItemForCopyItem(
                    itemId,
                    concept?.conceptId ?? 0,
                    (item?.title ?? "").escapeHTML(),
                )
                CNI.setAfterSave(function (_cniResult) {
                    // Nothing to do at the moment.
                })
                return ""
            case "MoveItemAction":
                CNI.clear()
                CNI.setAfterLoadingFields(function () {
                    if (CNI.title) {
                        CNI.title.value = item?.title ?? ""
                    }
                })
                CNI.setItemForMoveItem(
                    itemId,
                    concept?.conceptId ?? 0,
                    (item?.title ?? "").escapeHTML(),
                    concept?.conceptLabel ?? "",
                    false,
                )
                CNI.setAfterSave(function (_cniResult) {
                    // Nothing to do at the moment.
                })
                return ""
            case "CreateChildAction":
                // We currently have no way here to know whether this is a relation or a child collection,...
                // Therefore, at the moment we'll create it as a child of the same concept.
                CNI.clear()
                CNI.open(concept?.conceptId)
                return ""
            case "LinkParentAction":
                new window.Overlay(window.Overlay.extralarge)
                    .load(
                        window.TenForce.Execution.Web.Ajax.Service
                            .ShowSelectItemOverlay,
                        itemId,
                        Labels.Select,
                        Labels.PleaseSelectParentForCurrentItem,
                        window.itemPickerControllerClass.LinkToParent,
                    )
                    .setCallback(function (selectedIds) {
                        if (!selectedIds || !selectedIds[0]) {
                            return
                        }
                        // Not sure that we care much about passing down the concept ID here, might have side effects in GetItem from ItemBoxViewPresenter.cs
                        // selectedIds should only return one id because single select was activated.
                        window.TenForce.Execution.Web.Ajax.Service.LinkItem(
                            itemId,
                            Number(selectedIds[0].ItemId),
                            concept?.conceptId,
                            function (_item: unknown, _result: unknown) {
                                // Nothing to do at the moment.
                            },
                        )
                    })
                return ""
            case "EmailItemAction":
                new window.Overlay(window.Overlay.large).load(
                    window.TenForce.Execution.Web.Ajax.Service
                        .ShowEmailItemOverlay,
                    itemId,
                )
                return ""
            case "PrintItemAction":
                window.open(
                    getItemDetailSimplePrintUrl(env.applicationPath, itemId),
                )
                return ""
            case "ShowTimeRegistrationAction":
                new window.Overlay(window.Overlay.medium).load(
                    window.TenForce.Execution.Web.Ajax.Service
                        .ShowRegisteredTimeOverlay,
                    itemId,
                )
                return ""
            case "RegisterTimeAction":
                new window.Overlay(window.Overlay.medium).load(
                    window.TenForce.Execution.Web.Ajax.Service
                        .ShowRegisterTimeOverlay,
                    itemId,
                )
                return ""
            case "DeleteItemAction":
                if (
                    !confirm(
                        new window.Template(
                            window.Language.Labels.ConfirmDeletionItem,
                        ).evaluate({ title: item?.title ?? "" }),
                    )
                ) {
                    return ""
                }

                window.TenForce.Execution.Web.Ajax.Service.DeleteItem(
                    itemId,
                    null,
                    () => {
                        window.newUI.fields.refetchFields()
                    },
                )
                return ""
            case "QuickEditItemAction":
                CNI.clear()
                CNI.setAfterLoadingFields(function () {
                    const cni = window.cni.getInstance()
                    if (cni.title) {
                        cni.title.value = item?.title ?? ""
                    }
                })
                CNI.setItemForEditItem(
                    itemId,
                    concept?.conceptId ?? null,
                    parent?.id ?? null,
                    (item?.title ?? "").escapeHTML(),
                )
                CNI.setAfterSave(function () {
                    window.newUI.fields.refetchFields()
                })
                return ""
            default:
                console.log("Action not handled, defaulting to old behavior.")
                break
        }
    }

    return servicesAPI.postJSON(
        "ExecuteCardViewItemAction",
        payload,
        parseExecuteCardViewItemActionData,
    )
}

const parseCardViewData = (
    json: GetItemBoxDataResponseWrapper,
): CollectionView => {
    const {
        d: {
            Value: { PagingInfo, Items, allowADD },
        },
    } = json
    if (!PagingInfo) {
        throw new Error(`no pagingInfo in itemBoxResponse: ${json}`)
    }
    if (!Items) {
        throw new Error(`no items in itemBoxResponse: ${json}`)
    }
    if (allowADD === undefined) {
        throw new Error(`no allowADD in itemBoxResponse: ${json}`)
    }

    const paging = {
        currentPage: PagingInfo.SelectedPageIndex,
        totalNumberOfItems: PagingInfo.TotalNumberOfItems,
        pageSize: PagingInfo.ItemsPerPage,
    }

    return { paging, items: Items.map(parseItem), allowAdd: allowADD }
}

const parseCardViewItems = (json: GetItemsResponseWrapper): IItem[] => {
    const {
        d: { Value: Items },
    } = json

    if (!Items) {
        throw new Error(`no items in itemBoxResponse: ${json}`)
    }

    return Items.map(parseItem)
}

const parseItem = (responseItem: ItemBoxItem): IItem => {
    const { Properties } = responseItem

    const properties = {} as Record<string, unknown>
    Properties.forEach((p) => {
        properties[p.Name] = p.Value
    })

    const itemId = tryGetItemProperty<number>(ItemIdKey)
    const isNumberEnabled = tryGetItemProperty<boolean>(IsNumberEnabledKey)
    const listId = tryGetItemProperty<number>(ListIdKey)
    const listName = tryGetItemProperty<string>(ListNameKey)
    const workspaceId = tryGetItemProperty<number>(WorkspaceIdKey)
    const status = tryGetItemProperty<string>(StatusKey)
    const title = tryGetItemProperty<string>(TitleKey)
    const linkedDocuments = tryGetItemProperty<[]>(AttachmentsKey)
    const areAttachmentsEditable = tryGetItemProperty<boolean>(
        AttachmentsEditableKey,
    )
    const areAttachmentsEnabled = tryGetItemProperty<boolean>(
        AttachmentsEnabledKey,
    )

    let number
    if (isNumberEnabled) {
        number = tryGetItemProperty<string>(NumberKey)
    }

    return {
        id: itemId,
        listId,
        listName,
        workspaceId,
        title,
        number,
        status,
        areAttachmentsEditable,
        areAttachmentsEnabled,
        linkedDocuments,
    }

    function tryGetItemProperty<T>(propertyKey: string): T {
        const rawProperty = properties[propertyKey]
        if (typeof rawProperty === "undefined") {
            console.error(
                `item without ${propertyKey} property: ${responseItem}`,
            )
        }
        return rawProperty as T
    }
}

const parseItemActionsData = (json: ServicesResponse<string>): IButton[] => {
    const parsedResponse: ItemActionsData<ItemActionsDataValue> = JSON.parse(
        json.d,
    )
    const actions = parsedResponse.value.actions

    if (!actions) {
        throw new Error(`no actions in itemActions response: ${json}`)
    }

    return actions.map((a: ICallButton) => {
        const buttonData = {
            type: a.buttonData.type,
            actionId: a.buttonData.actionId,
            callData: a.buttonData.callData,
        }
        return {
            uuid: guid(),
            id: a.id,
            caption: a.caption,
            icon: a.icon,
            tooltip: a.tooltip,
            isLoading: false,
            isCustom: a.isCustom,
            inOptionalMenu: a.inOptionalMenu,
            showCaption: a.showCaption,
            buttonData,
        }
    })
}

const parseExecuteCardViewItemActionData = (
    json: ServicesResponse<string>,
): string => {
    const parsedResponse: ItemActionsData<string> = JSON.parse(json.d)
    return parsedResponse.value
}
